import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { BreakGlassService } from './break-glass.service';
import { FatipOidcAuthService } from './fatip-oidc.service';

/**
 * Visibility-me cache (Release 0.43.0).
 *
 * Calls org-service `GET /v1/visibility/me` once per identity-switch and
 * caches the result as a signal. Components that need to gate UI on
 * platform roles (`is_platform_admin`, `is_platform_operator`,
 * `is_lra_officer`) read `current()` synchronously.
 *
 * 0.62.0.2: Cache wird via OIDC-Subject-Change getriggert (Login/Logout/
 * Token-Refresh). Vorher: Persona-Switcher-Change. FakeAuthService
 * komplett entfernt.
 */
export interface VisibilityMe {
  identity_id: string;
  tenant_id: string;
  /** 0.72.217 (U33b, audit L54): the tenant's display name from the same
   *  GetTenant row, response-only — unset when the tenant carries no row
   *  (the synthetic default), in which case the account page shows the id
   *  alone. */
  tenant_display_name?: string;
  is_platform_admin: boolean;
  is_platform_operator: boolean;
  is_lra_officer: boolean;
  /** 0.45.0: read-only oversight role — sees the same module tiles as
   *  admin/operator but the server rejects every write endpoint. */
  is_auditor: boolean;
  full_access_org_ids: string[];
  breadcrumb_org_ids: string[];
  /** 0.46.0: orgs where the caller is Technical Organisation Manager
   *  (either directly granted `organisation:X#technical_manager` OR
   *  via the platform_admin escape hatch). The Datenanlieferung-tab
   *  enables the "Neue Anlieferung"-button when the current org's
   *  id is in this list. */
  technical_manager_org_ids: string[];
  /** 0.57.12 (ADR-015): orgs where the caller is HR-Officer.
   *  Berechtigt hr_always-Felder von Personen zu lesen, deren primäre
   *  Org in dieser Liste enthalten ist. */
  hr_officer_org_ids?: string[];
  /** 0.57.12 (ADR-015): EPA = Eidgenössisches Personalamt. Cross-org
   *  Platform-Role mit Read-Sicht auf alle hr_always-Felder. */
  is_epa_officer?: boolean;
  /** 0.57.12 (ADR-015): Person ist im Bund-Kontext = mind. 1 active
   *  workforce/privileged/external-Identity bei einer kind=internal_unit-
   *  Org. Bedingung für public-Klasse HR-Profile-Felder anderer
   *  Personen sehen. */
  is_in_bund_kontext?: boolean;
  /** 0.57.15 — Caller-Person-Lookup für Mein-Konto. Direkt-Zugriff auf
   *  die eigene Person für Edit-Calls (Sprachen, Social-Media, etc.).
   *  Leer wenn die Caller-Identity keiner Person zugeordnet ist
   *  (Service-Konten). */
  person_global_id?: string;
  person_display_name?: string;
  /** 0.57.48 ADR-029 — true wenn Caller Org hat die Grantor oder Grantee
   *  in aktivem Mandat ist. Mandate-Tile-Visibility hängt daran. */
  has_mandate_involvement?: boolean;
  /** 0.57.49 ADR-030 — true wenn Caller FullAccess auf Org mit scope IN
   *  (federal, cantonal, communal) hat. Frontend nutzt das für den
   *  "+ Neue API erstellen"-Button im apis-list (Privatwirtschaft sieht
   *  ihn gar nicht). Backend createApplication enforced denselben Gate. */
  is_publisher_eligible?: boolean;
}

@Injectable({ providedIn: 'root' })
export class VisibilityService {
  private http = inject(HttpClient);
  // 0.62.0.2: FakeAuthService -> FatipOidcAuthService (Persona-Switcher-
  // Removal Step 2/3). Re-fetch wird via OIDC-Subject getrackt statt
  // FakeAuth-current; seit U6 zusaetzlich via Break-Glass-Sitzung, siehe
  // Konstruktor.
  private oidc = inject(FatipOidcAuthService);
  // U6: the second session kind this cache has to follow, see the constructor.
  private bg = inject(BreakGlassService);

  private _current = signal<VisibilityMe | null>(null);
  readonly current = this._current.asReadonly();

  // U45: Zustand des Reads, additiv zum fail-closed null. 'loading' am Kopf
  // des Fetch-Pfads, 'loaded' nach dem Setzen von _current, 'failed' auf der
  // Fehlerseite. snapshot() bleibt null, bis eine erfolgreiche Antwort da ist.
  readonly status = signal<'loading' | 'loaded' | 'failed'>('loading');

  /** Snapshot for synchronous reads (e.g. inside computed signals).
   *  Returns null while the first fetch is in flight. */
  snapshot(): VisibilityMe | null { return this._current(); }

  isLRAOfficer = computed(() => this._current()?.is_lra_officer === true);
  isPlatformAdmin = computed(() => this._current()?.is_platform_admin === true);

  constructor() {
    // 0.62.0.2: Re-fetch whenever the OIDC-subject changes (login/logout).
    // Vorher: FakeAuth-Persona-Switcher-Trigger.
    //
    // U6 (2026-09-14, whole-branch review finding 7): the break-glass session
    // is tracked beside the OIDC subject. `break-glass-login.component.ts`
    // calls `bg.set(...)` and then navigates inside the single page, so no
    // OIDC signal moves and, until this line existed, no re-read happened: a
    // service constructed under an anonymous caller kept serving the anonymous
    // principal that `services/org/internal/auth/middleware.go` answers with,
    // deployment default tenant included, for the whole emergency session, and
    // the account page printed that tenant to the reader. The default is
    // spelled out in the spec rather than here, so that the six comments the
    // portal-wide literal gate lists stay six.
    //
    // `session()` and not `isActive()`: `isActive()` calls `clear()` on an
    // expired session, which writes a signal, and a write from inside the
    // effect that reads it is a loop waiting to happen. `session()` is a plain
    // readonly signal that moves on `set()` and on `clear()`, which is exactly
    // the pair of edges a re-read belongs on.
    //
    // The dependency runs this way round on purpose: `BreakGlassService` holds
    // the token and has no other dependency, and `bg.set()` runs from a login
    // component that may execute before this service exists at all.
    // Pinned by `core/auth/visibility-session-refresh.service.spec.ts`.
    effect(() => {
      this.oidc.subject(); // track
      this.bg.session();   // track
      this.refresh();
    });
  }

  async refresh() {
    this.status.set('loading');
    try {
      const v = await firstValueFrom(
        this.http.get<VisibilityMe>(`${environment.apiBaseUrl}/v1/visibility/me`),
      );
      this._current.set(v);
      this.status.set('loaded');
    } catch {
      // Fail closed — null means "no permissions known".
      this._current.set(null);
      this.status.set('failed');
    }
  }
}
